import { useAppDispatch } from '@/app/hooks';
import { Button } from '@/components/ui/button';
import { InputLabel } from '@/components/ui/InputLabel';
import type {
  WorkspaceVisualIdentity,
  WorkspaceVisualIdentityFontFamily,
  WorkspaceVisualIdentityTheme,
} from '@/features/workspace/types';
import { useSelector } from 'react-redux';
import type { RootState } from '@/app/store';
import { updateVisualIdentity } from '@/features/workspace/workspaceThunks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Loader from '@/components/ui/Loader';

export default function VisualColors({
  visualIdentity,
  setAccentColor,
  accentColor,
  backgroundColor,
  setBackgroundColor,
  secondaryBackgroundColor,
  setSecondaryBackgroundColor,
  contrastColor,
  setContrastColor,
  setDefaultIdentity,
  onSaveSuccess,
  theme,
}: {
  visualIdentity?: WorkspaceVisualIdentity | null;
  setAccentColor: (color: string) => void;
  accentColor: string;
  backgroundColor: string;
  setBackgroundColor: (color: string) => void;
  secondaryBackgroundColor: string;
  setSecondaryBackgroundColor: (color: string) => void;
  contrastColor: string;
  setContrastColor: (color: string) => void;
  setDefaultIdentity: (value: boolean) => void;
  onSaveSuccess?: () => void;
  theme?: WorkspaceVisualIdentityTheme;
}) {
  const { t } = useTranslation('webinars');
  const dispatch = useAppDispatch();
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);

  const [isSubmit, setIsSubmit] = useState(false);

  const [gradient1Color1, setGradient1Color1] = useState(
    visualIdentity?.gradient1Color1 || '#000000',
  );
  const [gradient1Color2, setGradient1Color2] = useState(
    visualIdentity?.gradient1Color2 || '#000000',
  );
  const [gradient2Color1, setGradient2Color1] = useState(
    visualIdentity?.gradient2Color1 || '#000000',
  );
  const [gradient2Color2, setGradient2Color2] = useState(
    visualIdentity?.gradient2Color2 || '#000000',
  );

  const handleSaveColors = async () => {
    setIsSubmit(true);
    try {
      const result = await dispatch(
        updateVisualIdentity({
          workspaceId: currentWorkspace?.id as number,
          input: {
            fontFamily: visualIdentity?.fontFamily as WorkspaceVisualIdentityFontFamily,
            theme: (theme || visualIdentity?.theme) as WorkspaceVisualIdentityTheme,
            accentColor: accentColor.trim(),
            backgroundColor: backgroundColor.trim(),
            secondaryBackgroundColor: secondaryBackgroundColor.trim(),
            contrastColor: contrastColor.trim(),
            gradient1Color1: gradient1Color1.trim(),
            gradient1Color2: gradient1Color2.trim(),
            gradient2Color1: gradient2Color1.trim(),
            gradient2Color2: gradient2Color2.trim(),
            defaultIdentity: false,
          },
        }),
      );
      if (updateVisualIdentity.fulfilled.match(result)) {
        toast.success(t('visualIdentity.colors.saveSuccess'));
        onSaveSuccess?.();
      }
    } finally {
      setIsSubmit(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4 justify-center items-center">
        <div className="w-full">
          <Button
            variant="default"
            className="w-full"
            onClick={handleSaveColors}
            disabled={isSubmit}
          >
            {isSubmit && <Loader />}
            {t('visualIdentity.colors.save')}
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <input
          type="color"
          value={accentColor}
          onChange={(e) => {
            setAccentColor(e.target.value);
            setDefaultIdentity(false);
          }}
          className="sr-only "
          id="accentColor"
        />
        <label
          htmlFor="accentColor"
          className="size-12 shrink-0 rounded-lg border border-input cursor-pointer"
          style={{ backgroundColor: accentColor }}
        ></label>
        <InputLabel
          className="w-full"
          label={t('visualIdentity.colors.accentColor')}
          value={accentColor}
          onChange={(e) => {
            setAccentColor(e.target.value);
            setDefaultIdentity(false);
          }}
        />
      </div>

      <div className="flex items-center gap-4">
        <input
          type="color"
          value={backgroundColor}
          onChange={(e) => {
            setBackgroundColor(e.target.value);
            setDefaultIdentity(false);
          }}
          className="sr-only shrink-0"
          id="backgroundColor"
        />
        <label
          htmlFor="backgroundColor"
          className="size-12 shrink-0 rounded-lg border border-input cursor-pointer"
          style={{ backgroundColor: backgroundColor }}
        ></label>
        <InputLabel
          className="w-full"
          label={t('visualIdentity.colors.backgroundColor')}
          value={backgroundColor}
          onChange={(e) => {
            setBackgroundColor(e.target.value);
            setDefaultIdentity(false);
          }}
        />
      </div>

      <div className="flex items-center gap-4">
        <input
          type="color"
          value={secondaryBackgroundColor}
          onChange={(e) => {
            setSecondaryBackgroundColor(e.target.value);
            setDefaultIdentity(false);
          }}
          className="sr-only shrink-0"
          id="secondaryBackgroundColor"
        />
        <label
          htmlFor="secondaryBackgroundColor"
          className="size-12 shrink-0 rounded-lg border border-input cursor-pointer"
          style={{ backgroundColor: secondaryBackgroundColor }}
        ></label>
        <InputLabel
          className="w-full"
          label={t('visualIdentity.colors.secondaryBackgroundColor')}
          value={secondaryBackgroundColor}
          onChange={(e) => {
            setSecondaryBackgroundColor(e.target.value);
            setDefaultIdentity(false);
          }}
        />
      </div>

      <div className="flex items-center gap-4">
        <input
          type="color"
          value={contrastColor}
          onChange={(e) => {
            setContrastColor(e.target.value);
            setDefaultIdentity(false);
          }}
          className="sr-only shrink-0"
          id="contrastColor"
        />
        <label
          htmlFor="contrastColor"
          className="size-12 shrink-0 rounded-lg border border-input cursor-pointer"
          style={{ backgroundColor: contrastColor }}
        ></label>
        <InputLabel
          className="w-full"
          label={t('visualIdentity.colors.contrastColor')}
          value={contrastColor}
          onChange={(e) => {
            setContrastColor(e.target.value);
            setDefaultIdentity(false);
          }}
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex items-center gap-4">
          <div className="size-12 shrink-0 rounded-lg border border-input bg-white"></div>
          <InputLabel
            className="w-full"
            label={t('visualIdentity.colors.white')}
            value="#FFFFFF"
            disabled
          />
        </div>

        <div className="flex items-center gap-4">
          <div className="size-12 shrink-0 rounded-lg border border-input bg-black"></div>
          <InputLabel
            className="w-full"
            label={t('visualIdentity.colors.black')}
            value="#000000"
            disabled
          />
        </div>
      </div>

      <div className="text-base text-text-secondary">{t('visualIdentity.colors.gradients')}</div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div
          className="size-12 rounded-lg border border-input shrink-0"
          style={{
            background: `linear-gradient(to bottom , ${gradient1Color1}, ${gradient1Color2})`,
          }}
        />
        <div className="flex w-full gap-4">
          <InputLabel
            className="w-full"
            label={t('visualIdentity.colors.color1')}
            value={gradient1Color1}
            onChange={(e) => {
              setGradient1Color1(e.target.value);
              setDefaultIdentity(false);
            }}
          />

          <InputLabel
            className="w-full"
            label={t('visualIdentity.colors.color2')}
            value={gradient1Color2}
            onChange={(e) => {
              setGradient1Color2(e.target.value);
              setDefaultIdentity(false);
            }}
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div
          className="size-12 rounded-lg border border-input shrink-0"
          style={{
            background: `linear-gradient(226deg, ${gradient2Color1} 0.2%, ${gradient2Color2} 104.63%)`,
          }}
        />
        <div className="flex w-full gap-4">
          <InputLabel
            className="w-full"
            label={t('visualIdentity.colors.color1')}
            value={gradient2Color1}
            onChange={(e) => {
              setGradient2Color1(e.target.value);
              setDefaultIdentity(false);
            }}
          />

          <InputLabel
            className="w-full"
            label={t('visualIdentity.colors.color2')}
            value={gradient2Color2}
            onChange={(e) => {
              setGradient2Color2(e.target.value);
              setDefaultIdentity(false);
            }}
          />
        </div>
      </div>
    </div>
  );
}
